import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { debounce } from "lodash"; // Install lodash or use custom debounce below
import { useLocation, useNavigate } from "react-router-dom";
import IconButton from "@mui/material/IconButton";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import ClearIcon from "@mui/icons-material/Clear";
import BusinessIcon from "@mui/icons-material/Business";
import VerifiedIcon from "@mui/icons-material/Verified";
import Pagination from "@mui/material/Pagination";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import ConfirmDialog from "components/Dialogs/ConfirmDialog";
import EditDialog from "components/Dialogs/EditDialog";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import DataTable from "examples/Tables/DataTable";
import MDAvatar from "components/MDAvatar";
import { getAdmins, getUsers, getPlansByRole, deleteUser, changeUserPlan, updateUser } from "data/controllers/usersController";
import { updateAdmin } from "data/controllers/usersController";

const adminColumns = [
  { Header: "admin", accessor: "admin", width: "30%", align: "left" },
  { Header: "phone", accessor: "phone", align: "left" },
  { Header: "created at", accessor: "createdAt", align: "center" },
  { Header: "action", accessor: "action", align: "center" },
];

const userColumns = [
  { Header: "user", accessor: "user", width: "30%", align: "left" },
  { Header: "role", accessor: "role", align: "center" },
  { Header: "plan", accessor: "plan", align: "center" },
  { Header: "phone", accessor: "phone", align: "left" },
  { Header: "created at", accessor: "createdAt", align: "center" },
  { Header: "action", accessor: "action", align: "center" },
];

function Users() {
  const location = useLocation();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]); // Admin rows
  const [userRows, setUserRows] = useState([]); // User rows
  const [userPage, setUserPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [sortOption, setSortOption] = useState("newToOld");
  const [isLoading, setIsLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [adminDialogOpen, setAdminDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedAdmin, setSelectedAdmin] = useState(null);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [pendingPlanChange, setPendingPlanChange] = useState(null);
  const [pendingUserId, setPendingUserId] = useState(null);
  const [isInitialLoad, setIsInitialLoad] = useState(true); // Flag for initial load

  // Use useRef to store role plans cache without triggering re-renders
  const rolePlansCache = useRef({});

  const sortOptions = [
    { label: "New to Old", value: "newToOld" },
    { label: "Old to New", value: "oldToNew" },
    { label: "Alphabetically", value: "alphabetically" },
  ];

  // Debounce search term
  const debouncedSetSearchTerm = useCallback(
    debounce((value) => {
      setDebouncedSearchTerm(value);
      setUserPage(1);
    }, 500),
    []
  );

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    debouncedSetSearchTerm(event.target.value);
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter") {
      setUserPage(1);
    }
  };

  const handleClearSearch = () => {
    setSearchTerm("");
    setDebouncedSearchTerm("");
    setUserPage(1);
  };

  // Get plans for a role using ref-based cache
  const getPlanOptionsForRole = useCallback(async (role) => {
    if (rolePlansCache.current[role]) {
      return rolePlansCache.current[role];
    }
    try {
      console.log(`Fetching plans for role: ${role}`); // Debug log
      const response = await getPlansByRole(role);
      const plans = response.data;
      const plansWithNone = plans.length === 0 ? ["None"] : ["None", ...plans];
      rolePlansCache.current[role] = plansWithNone;
      return plansWithNone;
    } catch (error) {
      console.error("Error fetching plans for role:", role, error);
      return ["None"];
    }
  }, []);

  // Initialize role filter and fetch data in a single effect
  useEffect(() => {
    let isMounted = true; // Prevent state updates after unmount

    async function initializeAndFetch() {
      const searchParams = new URLSearchParams(location.search);
      const roleParam = searchParams.get("role");
      const initialRole = ["candidate", "recruiter", 'unknown'].includes(roleParam) ? roleParam : "all";

      if (isMounted) {
        setRoleFilter(initialRole);
        setIsInitialLoad(false); // Mark initial load complete after setting roleFilter
      }

      if (!isInitialLoad) {
        setIsLoading(true);
        try {
          console.log("Fetching data..."); // Debug log
          // Fetch admins
          const admins = await getAdmins();
          const adminRows = admins.map((admin) => ({
            admin: (
              <MDBox display="flex" alignItems="center" lineHeight={1}>
                <MDAvatar src={admin.profilePicture} name={admin.name} size="sm" />
                <MDBox ml={2} lineHeight={1}>
                  <MDTypography display="block" variant="button" fontWeight="medium">
                    {admin.name}
                  </MDTypography>
                  <MDTypography variant="caption">{admin.email}</MDTypography>
                </MDBox>
              </MDBox>
            ),
            phone: (
              <MDTypography variant="caption" color="text" fontWeight="medium">
                +91 {admin.phone}
              </MDTypography>
            ),
            createdAt: (
              <MDTypography variant="caption" color="text" fontWeight="medium">
                {admin.createdAt
                  ? new Date(admin.createdAt).toLocaleString("en-IN", {
                    dateStyle: "medium",
                    timeStyle: "short",
                  })
                  : ""}
              </MDTypography>
            ),
            action: (
              <MDBox display="flex" alignItems="center" gap={1}>
                <IconButton size="small" color="info" onClick={() => handleEditAdmin(admin)}>
                  <EditIcon fontSize="small" />
                </IconButton>
              </MDBox>
            ),
            rawAdmin: admin, // Store raw admin data for edit
          }));
          setRows(adminRows);

          // Fetch users
          console.log(`Fetching users: page=${userPage}, search=${debouncedSearchTerm}, role=${roleFilter}, sort=${sortOption}`); // Debug log
          const { users, pagination } = await getUsers(userPage, 10, debouncedSearchTerm, roleFilter, sortOption);

          // Fetch plans for unique roles
          const uniqueRoles = [...new Set(users.map((user) => user.role || "candidate"))];
          const plansMap = {};
          for (const role of uniqueRoles) {
            plansMap[role] = await getPlanOptionsForRole(role);
          }

          // Format user rows
          const formattedUsers = users.map((user) => {
            const userRole = user.role || "candidate";
            const userPlans = plansMap[userRole] || ["None"];
            return {
              userId: user.id,
              userName: user.personalDetails?.fullName || "Unknown User",
              userRole,
              createdAtDate: user.createdAt ? new Date(user.createdAt) : null,
              user: (
                <MDBox display="flex" alignItems="center" lineHeight={1}>
                  <MDAvatar
                    src={userRole === "recruiter" ? user.companyDetails?.companyLogo : user.personalDetails?.profilePicture}
                    name={user.personalDetails?.fullName || "Unknown User"}
                    size="sm"
                  />
                  <MDBox ml={2} lineHeight={1} sx={{ maxWidth: "250px", overflowWrap: "break-word" }}>
                    <MDBox display="flex" alignItems="center" gap={0.5}>
                      <MDTypography display="block" variant="button" fontWeight="medium">
                        {user.personalDetails?.fullName || "Unknown User"}
                      </MDTypography>
                      {userRole === "recruiter" && user.isProfileVerified && (
                        <VerifiedIcon sx={{ fontSize: "16px", color: "#03a9f4", ml: 0.5 }} title="Verified Recruiter" />
                      )}
                    </MDBox>
                    {userRole === "recruiter" && (
                      <MDBox display="flex" alignItems="center" sx={{ mt: 0.5 }}>
                        <BusinessIcon sx={{ fontSize: "14px", color: "text.secondary", mr: 0.5 }} />
                        <MDTypography
                          variant="caption"
                          color="text.primary"
                          sx={{ whiteSpace: "normal", wordBreak: "break-word", maxWidth: "220px", fontWeight: "medium" }}
                        >
                          {user.companyDetails?.companyName || "Unknown Company"}
                        </MDTypography>
                      </MDBox>
                    )}
                    <MDTypography
                      variant="caption"
                      color="text.secondary"
                      sx={{ whiteSpace: "normal", wordBreak: "break-word", maxWidth: "250px", display: "block" }}
                    >
                      {userRole === "recruiter"
                        ? user.companyDetails?.location?.city || user.companyDetails?.location?.state
                          ? [user.companyDetails?.location?.city, user.companyDetails?.location?.state].filter(Boolean).join(", ")
                          : ""
                        : user.personalDetails?.location?.city || user.personalDetails?.location?.state
                          ? [user.personalDetails?.location?.city, user.personalDetails?.location?.state].filter(Boolean).join(", ")
                          : ""}
                    </MDTypography>
                  </MDBox>
                </MDBox>
              ),
              role: (
                <MDTypography variant="caption" color="text" fontWeight="medium">
                  {userRole}
                </MDTypography>
              ),
              plan: (
                <Select
                  value={
                    user.subscriptionPlan?.planName && user.subscriptionPlan.planName.trim() !== ""
                      ? user.subscriptionPlan.planName
                      : "None"
                  }
                  onChange={(e) => handlePlanChange(user.id || user.email, e.target.value)}
                  sx={{ height: "30px", fontSize: "14px", "& .MuiSelect-select": { padding: "4px 24px 4px 8px" } }}
                >
                  {userPlans.map((plan) => (
                    <MenuItem key={plan} value={plan}>
                      {plan}
                    </MenuItem>
                  ))}
                </Select>
              ),
              phone: (
                <MDTypography variant="caption" color="text" fontWeight="medium">
                  +91 {user.phone}
                </MDTypography>
              ),
              createdAt: (
                <MDTypography variant="caption" color="text" fontWeight="medium">
                  {user.createdAt
                    ? new Date(user.createdAt).toLocaleString("en-IN", {
                      dateStyle: "medium",
                      timeStyle: "short",
                    })
                    : ""}
                </MDTypography>
              ),
              action: (
                <MDBox display="flex" alignItems="center" gap={1}>
                  <IconButton size="small" color="info" onClick={() => handleEditUser(user)}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" color="error" onClick={() => handleDeleteUser(user)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </MDBox>
              ),
              rawUser: user, // Store raw user data for edit/delete
            };
          });

          if (isMounted) {
            setUserRows(formattedUsers);
            setTotalPages(Math.ceil((pagination?.total || 0) / 10));
          }
        } catch (error) {
          console.error("Error fetching data:", error);
          if (isMounted) {
            setDialogMode("error");
            setDialogOpen(true);
          }
        } finally {
          if (isMounted) {
            setIsLoading(false);
          }
        }
      }
    }

    initializeAndFetch();

    return () => {
      isMounted = false; // Cleanup to prevent state updates after unmount
    };
  }, [userPage, debouncedSearchTerm, roleFilter, sortOption, isInitialLoad]);

  // Memoize formatted user rows
  const formattedUserRows = useMemo(() => {
    return userRows.map((row) => ({
      ...row,
      plan: (
        <Select
          value={row.plan.props.value || "None"}
          onChange={(e) => handlePlanChange(row.userId, e.target.value)}
          sx={{ height: "30px", fontSize: "14px", "& .MuiSelect-select": { padding: "4px 24px 4px 8px" } }}
        >
          {(rolePlansCache.current[row.userRole] || ["None"]).map((plan) => (
            <MenuItem key={plan} value={plan}>
              {plan}
            </MenuItem>
          ))}
        </Select>
      ),
      action: (
        <MDBox display="flex" alignItems="center" gap={1}>
          <IconButton size="small" color="info" onClick={() => handleEditUser(row.rawUser)}>
            <EditIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" color="error" onClick={() => handleDeleteUser(row.rawUser)}>
            <DeleteIcon fontSize="small" />
          </IconButton>
        </MDBox>
      ),
    }));
  }, [userRows]);

  const handleEditUser = (user) => {
    setSelectedUser(user);
    setDialogMode("edit");
    setDialogOpen(true);
  };

  const handleDeleteUser = (user) => {
    setSelectedUser(user);
    setDialogMode("delete");
    setDialogOpen(true);
  };

  const deleteSelectedUser = async () => {
    setDialogOpen(false);
    if (selectedUser) {
      try {
        await deleteUser(selectedUser.id);
        setUserRows((prevRows) => prevRows.filter((row) => row.userId !== selectedUser.id));
        setSelectedUser(null);
      } catch (error) {
        console.error("Error deleting user:", error);
        setDialogMode("error");
        setSelectedUser(null);
        setDialogOpen(true);
      }
    }
  };

  const handleEditAdmin = (admin) => {
    setSelectedAdmin(admin);
    setAdminDialogOpen(true);
  };

  const handlePlanChange = (userId, newPlan) => {
    setPendingUserId(userId);
    setPendingPlanChange(newPlan);
    setConfirmDialogOpen(true);
  };

  const confirmPlanChange = async () => {
    if (pendingUserId && pendingPlanChange) {
      try {
        const user = await changeUserPlan(pendingUserId, pendingPlanChange);
        console.log("User plan changed successfully:", user);
        setUserRows((prevRows) =>
          prevRows.map((row) =>
            row.userId === user.id
              ? {
                ...row,
                plan: (
                  <Select
                    value={pendingPlanChange || "None"}
                    onChange={(e) => handlePlanChange(row.userId, e.target.value)}
                    sx={{ height: "30px", fontSize: "14px", "& .MuiSelect-select": { padding: "4px 24px 4px 8px" } }}
                  >
                    {(rolePlansCache.current[row.userRole] || ["None"]).map((plan) => (
                      <MenuItem key={plan} value={plan}>
                        {plan}
                      </MenuItem>
                    ))}
                  </Select>
                ),
                action: (
                  <MDBox display="flex" alignItems="center" gap={1}>
                    <IconButton size="small" color="info" onClick={() => handleEditUser(user)}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" color="error" onClick={() => handleDeleteUser(user)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </MDBox>
                ),
                rawUser: user, // Store raw user data for edit/delete
              }
              : row
          )
        );
      } catch (error) {
        console.error("Error changing user plan:", error);
        setDialogMode("error");
        setDialogOpen(true);
      }
    }
    setConfirmDialogOpen(false);
    setPendingUserId(null);
    setPendingPlanChange(null);
  };

  const handleSortChange = (event) => {
    setSortOption(event.target.value);
    setUserPage(1);
  };

  const handleRoleFilterChange = useCallback(
    (event) => {
      const selectedRole = event.target.value;
      setRoleFilter(selectedRole);
      setUserPage(1);
      const newSearchParams = new URLSearchParams();
      if (selectedRole !== "all") {
        newSearchParams.set("role", selectedRole);
      }
      navigate(`/users${newSearchParams.toString() ? `?${newSearchParams}` : ""}`, { replace: true });
    },
    [navigate]
  );

  const handlePageChange = (event, value) => {
    setUserPage(value);
  };

  const getEditableFieldsByRole = (role, user) => {
    const profileSettingsFields = [
      { label: "Is Profile Verified", key: "isProfileVerified", type: "checkbox", required: false, group: "profile" },
      { label: "Is Profile Blocked", key: "isProfileBlocked", type: "checkbox", required: false, group: "profile" },
      { label: "Is Personal Email Verified", key: "isPersonalEmailVerified", type: "checkbox", required: false, group: "profile" },
    ];

    if (role === "recruiter") {
      profileSettingsFields.push({
        label: "Is Company Email Verified",
        key: "isCompanyEmailVerified",
        type: "checkbox",
        required: false,
        group: "profile",
      });
    }

    const subscriptionFields = [
      { label: "Plan Name", key: "currentPlan", type: "text", required: false, disabled: true, group: "subscription" },
      {
        label: "AI Cache Duration Limit",
        key: "aiCacheLimit",
        type: "select",
        options: [
          { value: 0, label: "0 minutes" },
          { value: 60, label: "60 minutes" },
          { value: 120, label: "120 minutes" },
          { value: 180, label: "180 minutes" },
          { value: 300, label: "300 minutes" },
        ],
        required: true,
        group: "subscription",
      },
      { label: "Expires At", key: "expiresAt", type: "datetime-local", required: false, group: "subscription" },
    ];

    if (role === "recruiter") {
      subscriptionFields.push({
        label: "Is Crown Provided",
        key: "isCrownProvided",
        type: "checkbox",
        required: false,
        group: "subscription",
      });
    }

    if (role === "candidate") {
      subscriptionFields.splice(2, 0, {
        label: "Daily Apply Limit",
        key: "dailyApplyLimit",
        type: "number",
        required: true,
        group: "subscription",
      });
    } else if (role === "recruiter") {
      subscriptionFields.splice(
        2,
        0,
        { label: "Daily Post Limit", key: "totalFreePosts", type: "number", required: true, group: "subscription" },
        { label: "Daily Resume Limit", key: "perDayResumeLimit", type: "number", required: true, group: "subscription" }
      );
    }

    const topLevelFields = [
      { label: "Profile Picture", key: "profilePicture", type: "image", required: false },
      { label: "Full Name", key: "fullName", type: "text", required: true },
      { label: "Personal Email", key: "personalEmail", type: "text", required: false },
    ];

    const personalDetailsFields = [
      {
        label: "Gender",
        key: "gender",
        type: "select",
        options: [
          { value: "female", label: "Female" },
          { value: "male", label: "Male" },
          { value: "other", label: "Other" },
        ],
        required: false,
        group: "personal",
      },
      { label: "Introduction", key: "introduction", type: "textarea", required: false, group: "personal" },
      { label: "Street Address", key: "streetAddress", type: "text", required: false, group: "personal" },
      { label: "City", key: "city", type: "text", required: false, group: "personal" },
      { label: "State", key: "state", type: "text", required: false, group: "personal" },
    ];

    const educationFields = [
      { label: "College Name", key: "collegeName", type: "text", required: false, group: "education" },
      { label: "Is Currently Pursuing", key: "currentlyPursuing", type: "checkbox", required: false, group: "education" },
      { label: "Pursuing What", key: "pursuingWhat", type: "text", required: false, group: "education" },
      { label: "Degree", key: "degree", type: "text", required: false, group: "education" },
      { label: "Specialization", key: "specialization", type: "text", required: false, group: "education" },
    ];

    const experienceFields = (user.experienceDetails || []).flatMap((_, index) => [
      { label: `Experience ${index + 1} Company Name`, key: `expCompanyName_${index}`, type: "text", group: "experience", required: false },
      { label: `Experience ${index + 1} Job Role`, key: `jobRole_${index}`, type: "text", group: "experience", required: false },
      {
        label: `Experience ${index + 1} Start Date`,
        key: `startDate_${index}`,
        type: "date",
        group: "experience",
        inputProps: { max: new Date().toISOString().slice(0, 10) },
        required: false,
      },
      {
        label: `Experience ${index + 1} End Date`,
        key: `endDate_${index}`,
        type: "date",
        group: "experience",
        inputProps: { max: new Date().toISOString().slice(0, 10) },
        required: false,
      },
      { label: `Experience ${index + 1} Currently Working`, key: `currentlyWorking_${index}`, type: "checkbox", group: "experience", required: false },
    ]);

    const preferredJobFields = [
      { label: "Preferred Job Roles (comma-separated)", key: "jobRoles", type: "textarea", group: "preferred", required: false },
      { label: "Preferred Industry Types (comma-separated)", key: "industryTypes", type: "textarea", group: "preferred", required: false },
      { label: "Preferred Cities (comma-separated)", key: "cities", type: "textarea", group: "preferred", required: false },
      { label: "Preferred Skills (comma-separated)", key: "skills", type: "textarea", group: "preferred", required: false },
      { label: "Preferred Languages (comma-separated)", key: "languages", type: "textarea", group: "preferred", required: false },
      { label: "Resume URL", key: "resume", type: "text", group: "preferred", required: false },
    ];

    const companyFields = [
      { label: "Company Name", key: "companyName", type: "text", required: false, group: "company" },
      { label: "Company Email", key: "companyEmail", type: "text", required: false, group: "company" },
      { label: "Company Website", key: "companyWebsite", type: "text", required: false, group: "company" },
      { label: "Company Logo", key: "companyLogo", type: "image", required: false, group: "company" },
      { label: "Industry Type", key: "industryType", type: "text", required: false, group: "company" },
      { label: "Street Address", key: "companyStreetAddress", type: "text", required: false, group: "company" },
      { label: "City", key: "companyCity", type: "text", required: false, group: "company" },
      { label: "State", key: "companyState", type: "text", required: false, group: "company" },
      { label: "GSTIN", key: "gstin", type: "text", required: false, group: "company" },
    ];

    const adminFields = [
      { label: "Profile Picture", key: "profilePicture", type: "image", required: false },
      { label: "Name", key: "name", type: "text", required: true, group: "admin" },
      { label: "Email", key: "email", type: "text", required: true, group: "admin" },
      { label: "Phone", key: "phone", type: "text", required: true, group: "admin" },
    ];

    const normalizedRole = role || "candidate";
    if (normalizedRole === "candidate") {
      return [
        ...topLevelFields,
        ...personalDetailsFields,
        ...subscriptionFields,
        ...educationFields,
        ...experienceFields,
        ...preferredJobFields,
        ...profileSettingsFields,
        { key: "experienceEntries", type: "hidden" },
      ];
    } else if (normalizedRole === "recruiter") {
      return [...topLevelFields, ...personalDetailsFields, ...subscriptionFields, ...companyFields, ...profileSettingsFields];
    } else if (normalizedRole === "admin") {
      return adminFields;
    } else {
      return [
        ...topLevelFields,
        ...personalDetailsFields,
        ...subscriptionFields,
        ...educationFields,
        ...preferredJobFields,
        ...profileSettingsFields,
      ];
    }
  };

  const getFormDataByRole = (role, user) => {
    const normalizedRole = role || "candidate";
    const subscriptionData = {
      currentPlan:
        user.subscriptionPlan?.planName && user.subscriptionPlan.planName.trim() !== ""
          ? user.subscriptionPlan.planName
          : "None",
      isCrownProvided: user.subscriptionPlan?.isCrownProvided || false,
      totalFreePosts: user.subscriptionPlan?.totalFreePosts || 0,
      totalFreeResumes: user.subscriptionPlan?.totalFreeResumes || 0,
      perDayResumeLimit: user.subscriptionPlan?.perDayResumeLimit || 0,
      dailyApplyLimit: user.subscriptionPlan?.dailyApplyLimit || 0,
      aiCacheLimit: user.subscriptionPlan?.aiCacheLimit || 0,
      expiresAt: user.subscriptionPlan?.expiresAt
        ? new Date(user.subscriptionPlan.expiresAt).toISOString().slice(0, 16)
        : "",
    };

    const profileFlags = {
      isProfileVerified: user.isProfileVerified || false,
      isProfileBlocked: user.isProfileBlocked || false,
    };

    if (normalizedRole === "candidate") {
      const experiences = user.experienceDetails || [];
      const experienceData = experiences.reduce((acc, experience, index) => {
        acc[`expCompanyName_${index}`] = experience.companyName || "";
        acc[`jobRole_${index}`] = experience.jobRole || "";
        acc[`startDate_${index}`] = experience.startDate
          ? new Date(experience.startDate).toISOString().slice(0, 10)
          : "";
        acc[`endDate_${index}`] = experience.endDate ? new Date(experience.endDate).toISOString().slice(0, 10) : "";
        acc[`currentlyWorking_${index}`] = experience.currentlyWorking || false;
        return acc;
      }, {});

      return {
        ...subscriptionData,
        ...profileFlags,
        fullName: user.personalDetails?.fullName || "",
        personalEmail: user.personalDetails?.personalEmail || "",
        isPersonalEmailVerified: user.personalDetails?.isPersonalEmailVerified || false,
        profilePicture: user.personalDetails?.profilePicture || "",
        city: user.personalDetails?.location?.city || "",
        state: user.personalDetails?.location?.state || "",
        streetAddress: user.personalDetails?.location?.streetAddress || "",
        gender: user.personalDetails?.gender || "",
        introduction: user.personalDetails?.introduction || "",
        collegeName: user.educationDetails?.collegeName || "",
        degree: user.educationDetails?.degree || "",
        currentlyPursuing: user.educationDetails?.currentlyPursuing || false,
        pursuingWhat: user.educationDetails?.pursuingWhat || "",
        specialization: user.educationDetails?.specialization || "",
        ...experienceData,
        experienceEntries: experiences.length || 1,
        jobRoles: user.preferredJobDetails?.jobRoles?.join(", ") || "",
        industryTypes: user.preferredJobDetails?.industryTypes?.join(", ") || "",
        cities: user.preferredJobDetails?.cities?.join(", ") || "",
        skills: user.preferredJobDetails?.skills?.join(", ") || "",
        languages: user.preferredJobDetails?.languages?.join(", ") || "",
        resume: user.preferredJobDetails?.resume || "",
      };
    } else if (normalizedRole === "recruiter") {
      return {
        ...subscriptionData,
        ...profileFlags,
        fullName: user.personalDetails?.fullName || "",
        personalEmail: user.personalDetails?.personalEmail || "",
        isPersonalEmailVerified: user.personalDetails?.isPersonalEmailVerified || false,
        profilePicture: user.personalDetails?.profilePicture || "",
        city: user.personalDetails?.location?.city || "",
        state: user.personalDetails?.location?.state || "",
        streetAddress: user.personalDetails?.location?.streetAddress || "",
        gender: user.personalDetails?.gender || "",
        introduction: user.personalDetails?.introduction || "",
        companyName: user.companyDetails?.companyName || "",
        companyEmail: user.companyDetails?.companyEmail || "",
        companyWebsite: user.companyDetails?.companyWebsite || "",
        companyLogo: user.companyDetails?.companyLogo || "",
        industryType: user.companyDetails?.industryType || "",
        companyStreetAddress: user.companyDetails?.location?.streetAddress || "",
        companyCity: user.companyDetails?.location?.city || "",
        companyState: user.companyDetails?.location?.state || "",
        gstin: user.companyDetails?.gstin || "",
        isCompanyEmailVerified: user.companyDetails?.isCompanyEmailVerified || false,
      };
    } else if (normalizedRole === "admin") {
      return {
        name: user.name || "",
        email: user.email || "",
        phone: user.phone || "",
        profilePicture: user.profilePicture || "",
      };
    } else {
      return {
        ...subscriptionData,
        ...profileFlags,
        fullName: user.personalDetails?.fullName || "",
        personalEmail: user.personalDetails?.personalEmail || "",
        isPersonalEmailVerified: user.personalDetails?.isPersonalEmailVerified || false,
        profilePicture: user.personalDetails?.profilePicture || "",
        city: user.personalDetails?.location?.city || "",
        state: user.personalDetails?.location?.state || "",
        streetAddress: user.personalDetails?.location?.streetAddress || "",
        gender: user.personalDetails?.gender || "",
        introduction: user.personalDetails?.introduction || "",
      };
    }
  };

  const updateUserDataFromForm = (user, formData) => {
    const updated = { ...user };
    updated.subscriptionPlan = {
      ...updated.subscriptionPlan,
      totalFreePosts: Math.max(0, formData.totalFreePosts || 0),
      totalFreeResumes: Math.max(0, formData.totalFreeResumes || 0),
      perDayResumeLimit: Math.max(0, formData.perDayResumeLimit || 0),
      dailyApplyLimit: Math.max(0, formData.dailyApplyLimit || 0),
      aiCacheLimit: formData.aiCacheLimit || 0,
      expiresAt: formData.expiresAt ? new Date(formData.expiresAt).toISOString() : null,
      isCrownProvided: formData.isCrownProvided,
    };

    updated.isProfileVerified = formData.isProfileVerified;
    updated.isProfileBlocked = formData.isProfileBlocked;

    if (user.role === "candidate") {
      updated.personalDetails = {
        ...updated.personalDetails,
        fullName: formData.fullName,
        personalEmail: formData.personalEmail,
        isPersonalEmailVerified: formData.isPersonalEmailVerified,
        profilePicture: formData.profilePicture,
        gender: formData.gender,
        introduction: formData.introduction,
        location: { city: formData.city, state: formData.state, streetAddress: formData.streetAddress },
      };

      updated.educationDetails = {
        ...(updated.educationDetails || {}),
        collegeName: formData.collegeName,
        degree: formData.degree,
        currentlyPursuing: formData.currentlyPursuing,
        pursuingWhat: formData.pursuingWhat,
        specialization: formData.specialization,
      };

      const numExperiences = formData.experienceEntries || 1;
      updated.experienceDetails = Array.from({ length: numExperiences }, (_, index) => {
        const startDate = formData[`startDate_${index}`] ? new Date(formData[`startDate_${index}`]) : null;
        const endDate = formData[`endDate_${index}`] && !formData[`currentlyWorking_${index}`] ? new Date(formData[`endDate_${index}`]) : null;
        const isValidDateRange = !startDate || !endDate || endDate >= startDate;
        return {
          companyName: formData[`expCompanyName_${index}`] || "",
          jobRole: formData[`jobRole_${index}`] || "",
          startDate: startDate && isValidDateRange ? startDate.toISOString() : null,
          endDate: endDate && isValidDateRange ? endDate.toISOString() : null,
          currentlyWorking: formData[`currentlyWorking_${index}`] || false,
        };
      }).filter((exp) => exp.companyName || exp.jobRole || exp.startDate || exp.endDate);

      updated.preferredJobDetails = {
        ...updated.preferredJobDetails,
        jobRoles: formData.jobRoles ? formData.jobRoles.split(",").map((item) => item.trim()) : [],
        industryTypes: formData.industryTypes ? formData.industryTypes.split(",").map((item) => item.trim()) : [],
        cities: formData.cities ? formData.cities.split(",").map((item) => item.trim()) : [],
        skills: formData.skills ? formData.skills.split(",").map((item) => item.trim()) : [],
        languages: formData.languages ? formData.languages.split(",").map((item) => item.trim()) : [],
        resume: formData.resume,
      };
    } else if (user.role === "recruiter") {
      updated.personalDetails = {
        ...updated.personalDetails,
        fullName: formData.fullName,
        personalEmail: formData.personalEmail,
        isPersonalEmailVerified: formData.isPersonalEmailVerified,
        profilePicture: formData.profilePicture,
        gender: formData.gender,
        introduction: formData.introduction,
        location: { city: formData.city, state: formData.state, streetAddress: formData.streetAddress },
      };

      updated.companyDetails = {
        ...updated.companyDetails,
        companyName: formData.companyName,
        industryType: formData.industryType,
        companyEmail: formData.companyEmail,
        isCompanyEmailVerified: formData.isCompanyEmailVerified,
        companyWebsite: formData.companyWebsite,
        companyLogo: formData.companyLogo,
        location: { city: formData.companyCity, state: formData.companyState, streetAddress: formData.companyStreetAddress },
        gstin: formData.gstin || null,
      };
    } else if (user.role === "admin") {
      updated.name = formData.name;
      updated.email = formData.email;
      updated.phone = formData.phone;
      updated.profilePicture = formData.profilePicture;
    } else {
      updated.personalDetails = {
        ...updated.personalDetails,
        fullName: formData.fullName,
        personalEmail: formData.personalEmail,
        isPersonalEmailVerified: formData.isPersonalEmailVerified,
        profilePicture: formData.profilePicture,
        gender: formData.gender,
        introduction: formData.introduction,
        location: { city: formData.city, state: formData.state, streetAddress: formData.streetAddress },
      };
    }

    return updated;
  };

  const confirmAdminEdit = async (formData) => {
    if (!selectedAdmin) return;

    setAdminDialogOpen(false);

    try {
      const admin = await updateAdmin(selectedAdmin.id, selectedAdmin);

      // Update userRows with the new admin data
      setRows((prevRows) =>
        prevRows.map((row) =>
          row.rawAdmin.id === admin.id
            ? {
              ...row,
              admin: (
                <MDBox display="flex" alignItems="center" lineHeight={1}>
                  <MDAvatar src={admin.profilePicture} name={admin.name} size="sm" />
                  <MDBox ml={2} lineHeight={1}>
                    <MDTypography display="block" variant="button" fontWeight="medium">
                      {admin.name}
                    </MDTypography>
                    <MDTypography variant="caption">{admin.email}</MDTypography>
                  </MDBox>
                </MDBox>
              ),
              phone: (
                <MDTypography variant="caption" color="text" fontWeight="medium">
                  +91 {admin.phone}
                </MDTypography>
              ),
              createdAt: (
                <MDTypography variant="caption" color="text" fontWeight="medium">
                  {admin.createdAt
                    ? new Date(admin.createdAt).toLocaleString("en-IN", {
                      dateStyle: "medium",
                      timeStyle: "short",
                    })
                    : ""}
                </MDTypography>
              ),
              action: (
                <MDBox display="flex" alignItems="center" gap={1}>
                  <IconButton size="small" color="info" onClick={() => handleEditAdmin(admin)}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                </MDBox>
              ),
            }
            : row
        )
      );

      setSelectedAdmin(null);
    } catch (error) {
      console.error("Error updating admin:", error);
      setAdminDialogOpen(true);
    }
  };

  const confirmUserEdit = async (formData) => {
    if (!selectedUser) return;

    setDialogOpen(false);

    try {
      const user = await updateUser(selectedUser.id, selectedUser);

      // Update userRows with the new user data
      setUserRows((prevRows) =>
        prevRows.map((row) =>
          row.userId === user.id
            ? {
              ...row,
              user: (
                <MDBox display="flex" alignItems="center" lineHeight={1}>
                  <MDAvatar
                    src={user.role === "recruiter" ? user.companyDetails?.companyLogo : user.personalDetails?.profilePicture}
                    name={user.personalDetails?.fullName || "Unknown User"}
                    size="sm"
                  />
                  <MDBox ml={2} lineHeight={1} sx={{ maxWidth: "250px", overflowWrap: "break-word" }}>
                    <MDBox display="flex" alignItems="center" gap={0.5}>
                      <MDTypography display="block" variant="button" fontWeight="medium">
                        {user.personalDetails?.fullName || "Unknown User"}
                      </MDTypography>
                      {user.role === "recruiter" && user.isProfileVerified && (
                        <VerifiedIcon sx={{ fontSize: "16px", color: "#03a9f4", ml: 0.5 }} title="Verified Recruiter" />
                      )}
                    </MDBox>
                    {user.role === "recruiter" && (
                      <MDBox display="flex" alignItems="center" sx={{ mt: 0.5 }}>
                        <BusinessIcon sx={{ fontSize: "14px", color: "text.secondary", mr: 0.5 }} />
                        <MDTypography
                          variant="caption"
                          color="text.primary"
                          sx={{ whiteSpace: "normal", wordBreak: "break-word", maxWidth: "220px", fontWeight: "medium" }}
                        >
                          {user.companyDetails?.companyName || "Unknown Company"}
                        </MDTypography>
                      </MDBox>
                    )}
                    <MDTypography
                      variant="caption"
                      color="text.secondary"
                      sx={{ whiteSpace: "normal", wordBreak: "break-word", maxWidth: "250px", display: "block" }}
                    >
                      {user.role === "recruiter"
                        ? user.companyDetails?.location?.city || user.companyDetails?.location?.state
                          ? [user.companyDetails?.location?.city, user.companyDetails?.location?.state].filter(Boolean).join(", ")
                          : ""
                        : user.personalDetails?.location?.city || user.personalDetails?.location?.state
                          ? [user.personalDetails?.location?.city, user.personalDetails?.location?.state].filter(Boolean).join(", ")
                          : ""}
                    </MDTypography>
                  </MDBox>
                </MDBox>
              ),
              role: (
                <MDTypography variant="caption" color="text" fontWeight="medium">
                  {user.role || "candidate"}
                </MDTypography>
              ),
              plan: (
                <Select
                  value={
                    user.subscriptionPlan?.planName && user.subscriptionPlan.planName.trim() !== ""
                      ? user.subscriptionPlan.planName
                      : "None"
                  }
                  onChange={(e) => handlePlanChange(user.id, e.target.value)}
                  sx={{ height: "30px", fontSize: "14px", "& .MuiSelect-select": { padding: "4px 24px 4px 8px" } }}
                >
                  {(rolePlansCache.current[user.role || "candidate"] || ["None"]).map((plan) => (
                    <MenuItem key={plan} value={plan}>
                      {plan}
                    </MenuItem>
                  ))}
                </Select>
              ),
              phone: (
                <MDTypography variant="caption" color="text" fontWeight="medium">
                  +91 {user.phone}
                </MDTypography>
              ),
              createdAt: (
                <MDTypography variant="caption" color="text" fontWeight="medium">
                  {user.createdAt
                    ? new Date(user.createdAt).toLocaleString("en-IN", {
                      dateStyle: "medium",
                      timeStyle: "short",
                    })
                    : ""}
                </MDTypography>
              ),
              rawUser: user,
            }
            : row
        )
      );
      setSelectedUser(null);
    } catch (error) {
      console.error("Error updating user:", error);
      setDialogMode("error");
      setDialogOpen(true);
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox mx={2} mt={-3} py={{ xs: 2, sm: 3 }} px={2} variant="gradient" bgColor="info" borderRadius="lg" coloredShadow="info">
                <MDTypography variant="h6" color="white" sx={({ breakpoints }) => ({ fontSize: { xs: "1rem", sm: "1.25rem" } })}>
                  Admins Table
                </MDTypography>
              </MDBox>
              <MDBox pt={{ xs: 2, sm: 3 }} px={{ xs: 1, sm: 0 }}>
                <DataTable table={{ columns: adminColumns, rows }} isSorted={false} entriesPerPage={false} showTotalEntries={false} noEndBorder />
              </MDBox>
            </Card>
          </Grid>
          <Grid item xs={12}>
            <Card>
              <MDBox mx={2} mt={-3} py={{ xs: 2, sm: 3 }} px={2} variant="gradient" bgColor="info" borderRadius="lg" coloredShadow="info">
                <MDTypography variant="h6" color="white" sx={({ breakpoints }) => ({ fontSize: { xs: "1rem", sm: "1.25rem" } })}>
                  Users Table
                </MDTypography>
              </MDBox>
              <MDBox px={2} pt={2}>
                <MDBox
                  display="flex"
                  alignItems="center"
                  gap={2}
                  mb={2}
                  sx={{ flexDirection: { xs: "column", md: "row" }, alignItems: { xs: "stretch", md: "center" }, "& > *": { width: { xs: "100%", md: "auto" } } }}
                >
                  <MDBox display="flex" alignItems="center" sx={{ flex: { xs: "none", md: 1 }, width: { xs: "100%", md: "auto" }, minWidth: 0 }}>
                    <input
                      type="text"
                      placeholder="Search by name, email, or phone"
                      value={searchTerm}
                      onChange={handleSearchChange}
                      onKeyDown={handleKeyDown}
                      style={{ flex: 1, padding: "10px", fontSize: "14px", borderRadius: "6px", border: "1px solid #ccc", minWidth: 0 }}
                    />
                    {searchTerm && (
                      <IconButton onClick={handleClearSearch} size="small">
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    )}
                  </MDBox>
                  <MDBox display="flex" alignItems="center" gap={1} sx={{ minWidth: 0 }}>
                    <MDTypography variant="caption" fontWeight="medium" sx={{ whiteSpace: "nowrap", display: { xs: "none", sm: "block" } }}>
                      Filter:
                    </MDTypography>
                    <FormControl size="small" sx={{ flex: 1, minWidth: "120px" }}>
                      <Select
                        value={roleFilter}
                        onChange={handleRoleFilterChange}
                        displayEmpty
                        sx={{ height: "36px", fontSize: "14px", "& .MuiSelect-select": { padding: "8px 24px 8px 12px" } }}
                      >
                        <MenuItem value="all">All Users</MenuItem>
                        <MenuItem value="candidate">Candidates</MenuItem>
                        <MenuItem value="recruiter">Recruiters</MenuItem>
                        <MenuItem value="unknown">Unknown</MenuItem>
                      </Select>
                    </FormControl>
                  </MDBox>
                  <MDBox display="flex" alignItems="center" gap={1} sx={{ minWidth: 0 }}>
                    <MDTypography variant="caption" fontWeight="medium" sx={{ whiteSpace: "nowrap", display: { xs: "none", sm: "block" } }}>
                      Sort by:
                    </MDTypography>
                    <Select
                      value={sortOption}
                      onChange={handleSortChange}
                      sx={{ height: "36px", fontSize: "14px", flex: 1, minWidth: "140px", "& .MuiSelect-select": { padding: "8px 24px 8px 12px" } }}
                    >
                      {sortOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </MDBox>
                </MDBox>
              </MDBox>
              <MDBox pt={{ xs: 2, sm: 3 }} px={{ xs: 1, sm: 0 }}>
                {isLoading ? (
                  <MDBox display="flex" justifyContent="center" p={3}>
                    <MDTypography variant="body2">Loading...</MDTypography>
                  </MDBox>
                ) : (
                  <DataTable table={{ columns: userColumns, rows: formattedUserRows }} isSorted={false} entriesPerPage={false} showTotalEntries={false} noEndBorder />
                )}
              </MDBox>
              <MDBox display="flex" alignItems="center" justifyContent="center" mb={2} mt={2} px={3}>
                <Pagination
                  count={totalPages}
                  page={userPage}
                  onChange={handlePageChange}
                  color="primary"
                  showFirstButton={window.innerWidth > 768}
                  showLastButton={window.innerWidth > 768}
                  siblingCount={window.innerWidth > 768 ? 1 : 0}
                  boundaryCount={window.innerWidth > 768 ? 1 : 1}
                  sx={{
                    ml: { xs: 0, sm: 2 },
                    "& .MuiPaginationItem-root": { fontSize: { xs: "0.75rem", sm: "0.875rem" }, minWidth: { xs: "28px", sm: "32px" }, height: { xs: "28px", sm: "32px" } },
                  }}
                />
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />
      {dialogOpen && dialogMode === "delete" && (
        <ConfirmDialog
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          onConfirm={deleteSelectedUser}
          description={`Are you sure you want to delete ${selectedUser?.role === "recruiter"
            ? selectedUser?.companyDetails?.companyName || "this company"
            : selectedUser?.personalDetails?.fullName || "this user"
            }?`}
          confirmButtonText="Delete"
          confirmButtonColor="error"
        />
      )}
      {dialogOpen && dialogMode === "error" && (
        <ConfirmDialog
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          onConfirm={() => setDialogOpen(false)}
          description="An error occurred while processing your request. Please try again later."
          confirmButtonText="Okay"
          confirmButtonColor="info"
        />
      )}
      {dialogOpen && dialogMode === "edit" && selectedUser && (
        <EditDialog
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          title={`Edit ${selectedUser.role || "User"}`}
          fields={getEditableFieldsByRole(selectedUser.role || "candidate", selectedUser)}
          formData={getFormDataByRole(selectedUser.role || "candidate", selectedUser)}
          setFormData={(data) => {
            const updatedUser = updateUserDataFromForm(selectedUser, data);
            setSelectedUser(updatedUser);
          }}
          onSubmit={confirmUserEdit}
        />
      )}
      {adminDialogOpen && selectedAdmin && (
        <EditDialog
          open={adminDialogOpen}
          onClose={() => setAdminDialogOpen(false)}
          title="Edit Admin"
          fields={getEditableFieldsByRole("admin", selectedAdmin)}
          formData={getFormDataByRole("admin", selectedAdmin)}
          setFormData={(data) => {
            const updatedAdmin = updateUserDataFromForm(selectedAdmin, data);
            setSelectedAdmin(updatedAdmin);
          }}
          onSubmit={confirmAdminEdit}
        />
      )}
      {confirmDialogOpen && (
        <ConfirmDialog
          open={confirmDialogOpen}
          title="Confirm Plan Change"
          description={`Changing the plan to ${pendingPlanChange} will provide premium features free of cost. Are you sure you want to proceed?`}
          onClose={() => {
            setConfirmDialogOpen(false);
            setPendingUserId(null);
            setPendingPlanChange(null);
          }}
          onConfirm={confirmPlanChange}
          confirmButtonText="Confirm"
          confirmButtonColor="primary"
        />
      )}
    </DashboardLayout>
  );
}

export default React.memo(Users);